import { useState, useEffect } from 'react';
import { X, FlaskConical } from 'lucide-react';
import { usePropertyContext } from '@/context/PropertyContext';

const DEMO_BANNER_DISMISSED_KEY = 'el-demo-banner-dismissed';

/**
 * Demo mode banner - shown when property has isDemo: true
 * Dismissable but re-appears on page navigation (uses sessionStorage)
 */
export function DemoBanner() {
  const { isDemo } = usePropertyContext();
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if banner was dismissed in this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem(DEMO_BANNER_DISMISSED_KEY);
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  // Reset dismissed state when navigating (re-read from sessionStorage)
  useEffect(() => {
    const handleNavigation = () => {
      // Small delay to let the new page render
      setTimeout(() => {
        const dismissed = sessionStorage.getItem(DEMO_BANNER_DISMISSED_KEY);
        setIsDismissed(dismissed === 'true');
      }, 100);
    };

    window.addEventListener('popstate', handleNavigation);
    return () => window.removeEventListener('popstate', handleNavigation);
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem(DEMO_BANNER_DISMISSED_KEY, 'true');
  };

  // Clear the dismissed state on page unload so it reappears next session
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem(DEMO_BANNER_DISMISSED_KEY);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  if (!isDemo || isDismissed) {
    return null;
  }

  return (
    <div className="relative bg-amber-50 border-b border-amber-200">
      <div className="mx-auto max-w-7xl px-4 py-2.5 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-2 text-sm">
          <FlaskConical className="h-4 w-4 text-amber-600 shrink-0" />
          <span className="font-medium text-amber-800">Demo Mode</span>
          <span className="text-amber-700">
            &ndash; No real bookings will be created
          </span>
        </div>
      </div>
      <button
        onClick={handleDismiss}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded-md transition-colors"
        aria-label="Dismiss demo banner"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
